/**
 * Die Rechenschicht der Balkengriffe im Belegungskalender: wie breit eine
 * Trefferzone sein darf, und welcher Zeitraum aus einer Zugweite wird.
 *
 * ════════════════════════════════════════════════════════════════════════════
 *  WARUM DAS HIER STEHT UND NICHT IM KALENDER
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Dieses Projekt hat keine DOM-Testumgebung. Was nur in einer Komponente lebt,
 * ist ausschließlich von Hand prüfbar — und der Fehler, um den es hier geht,
 * ist von Hand gerade nicht zu finden: eine Nacht zu viel oder zu wenig sieht
 * im Kalender „ungefähr richtig" aus und fällt erst auf der Rechnung auf, dann
 * aber ist sie schon beim Gast. Also steht die Rechnung hier, rein, ohne React
 * und ohne DOM, mit Tests daneben.
 *
 * Dieselbe Überlegung gilt für die Frage, welcher Balken überhaupt einen Griff
 * bekommt (`gripsFor`): sie entscheidet, ob ein Vermieter mit dem Finger an
 * einer abgerechneten Buchung ziehen kann, und sie steht deshalb hier statt im
 * Kalender.
 */
import type { Booking } from '../data/model'
import { addDays, type ISODate } from './dates'

/** Welches Ende des Balkens angefasst wurde. */
export type BarEdge = 'start' | 'end'

/**
 * Die Zahlen aus dem ADR, an einer Stelle.
 *
 * `MAX_GRIP` ist der Deckel: mehr als 28 px braucht kein Griff, auch wenn der
 * Balken zehn Nächte breit ist. `MIN_GRIP` ist die Untergrenze, unter der es
 * lieber gar keinen Griff gibt als einen halb greifbaren — ein Griff, den man
 * nur manchmal trifft, ist schlimmer als keiner, weil er die Geste daneben
 * (das Verschieben) unzuverlässig macht.
 */
const MAX_GRIP = 28
const MIN_GRIP = 16
/** Mit Maus oder Trackpad zielt man genauer; dort genügen 12 px. */
const FINE_GRIP = 12
/** … solange in der Mitte noch 20 px zum Verschieben und Antippen bleiben. */
const FINE_MIN_MIDDLE = 20

/**
 * Griffbreite je Seite in px. `0` heißt: kein Griff, auf keiner der beiden Seiten.
 *
 * Bei grobem Zeiger (Finger) gilt `min(28, (Balkenbreite − Spaltenbreite) / 2)`.
 * Der Abzug einer ganzen Spaltenbreite ist der Kern der Formel: es bleibt immer
 * mindestens eine ganze Tagesspalte Mitte übrig. Die Tagesspalte ist die
 * Einheit, in der dieser Kalender denkt und in der der Finger zielt — eine
 * Mitte, die schmaler ist als ein Tag, ist keine Fläche mehr, sondern ein Rest.
 *
 * Auf einem Ein-Nacht-Balken bleibt damit nichts übrig, und das ist die
 * Entscheidung: **das Verschieben hat Vorrang.** Es ist im Kalender ohne
 * Alternative, die Längenänderung nicht — die steht im Buchungsblatt in zwei
 * Datumsfeldern.
 *
 * Nachkommastellen werden abgerundet. Ohne diese Festlegung hinge die
 * Trefferzone an Subpixeln, und ob 15,5 px nun ein Griff sind oder keiner,
 * wäre Auslegungssache statt Prüfergebnis.
 */
export function gripWidth(barWidth: number, dayWidth: number, coarse: boolean): number {
  // Entartete Eingaben (noch nicht gemessene Breite, Spaltenbreite 0) geben
  // keinen Griff — und vor allem kein NaN, das sich als Breite in den Stil
  // schriebe und dort niemandem mehr auffiele.
  if (!Number.isFinite(barWidth) || !Number.isFinite(dayWidth)) return 0
  if (barWidth <= 0 || dayWidth <= 0) return 0

  if (!coarse) {
    return barWidth - 2 * FINE_GRIP >= FINE_MIN_MIDDLE ? FINE_GRIP : 0
  }

  const grip = Math.floor(Math.min(MAX_GRIP, (barWidth - dayWidth) / 2))
  return grip >= MIN_GRIP ? grip : 0
}

/**
 * Die Zugweite in Pixeln als Verschiebung in ganzen Tagesspalten.
 *
 * ════════════════════════════════════════════════════════════════════════════
 *  DIE STELLE, AN DER ES UM EINEN TAG DANEBENGEHT
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Gerechnet wird mit einer Spalten**verschiebung**, nicht mit der Spalte unter
 * dem Zeiger. Der Balken beginnt in der Mitte der Anreisespalte und endet in
 * der Mitte der Abreisespalte — wer die Zeigerspalte direkt als neues Datum
 * nimmt, ist an der einen Kante um eine Nacht zu kurz, und wer blind `+1`
 * rechnet, an der anderen. Die Differenz kennt dieses Problem nicht, weil sie
 * von einem bereits richtigen Datum ausgeht.
 *
 * `Math.round` ist dieselbe Zeile, mit der das vorhandene Verschieben rechnet.
 * Sie ist bei genau einer halben Spalte unsymmetrisch (eine halbe Spalte nach
 * rechts verlängert, eine halbe nach links verkürzt nicht) — zwei verschiedene
 * Rundungen für zwei Gesten desselben Kalenders wären schlimmer als eine
 * unsymmetrische.
 */
export function dayShift(dx: number, dayWidth: number): number {
  if (!Number.isFinite(dx) || !Number.isFinite(dayWidth) || dayWidth <= 0) return 0
  // `|| 0` fängt die negative Null: `Math.round(-0.5)` ist in JavaScript `-0`,
  // und das ist ein eigener Wert, der über jeden strengen Vergleich stolpert.
  return Math.round(dx / dayWidth) || 0
}

/**
 * Neuer Zeitraum aus einer Spaltenverschiebung. Klemmt auf mindestens eine Nacht.
 *
 * Es bewegt sich immer nur **ein** Ende: am hinteren Griff bleibt die Anreise
 * stehen, am vorderen die Abreise. Ein Griff, der beide Enden bewegt, wäre ein
 * Verschieben mit falschem Namen.
 *
 * Die Klemmung ersetzt weder `badDates` im Buchungsblatt noch die Datenbank —
 * sie steht davor. Ein Aufenthalt ohne Nacht soll gar nicht erst als Vorschau
 * erscheinen; er ist keine Eingabe, die man abweisen müsste, sondern eine, die
 * es nicht zu geben braucht.
 */
export function resizedRange(
  arrival: ISODate, departure: ISODate, edge: BarEdge, shift: number,
): { arrival: ISODate; departure: ISODate } {
  if (edge === 'end') {
    const gezogen = addDays(departure, shift)
    const frueheste = addDays(arrival, 1)
    return { arrival, departure: gezogen > frueheste ? gezogen : frueheste }
  }
  const gezogen = addDays(arrival, shift)
  const spaeteste = addDays(departure, -1)
  return { arrival: gezogen < spaeteste ? gezogen : spaeteste, departure }
}

/** Welche Enden dieses Balkens sich anfassen lassen. */
export interface BarGrips {
  /** Der vordere Griff — die Anreise. */
  start: boolean
  /** Der hintere Griff — die Abreise. */
  end: boolean
}

/**
 * Wer einen Griff bekommt, und an welchem Ende.
 *
 * `locked` ist die Antwort aus `lib/bookingLock.ts`: gibt es zu dieser Buchung
 * eine ausgestellte Rechnung? Dann sind Anreise, Abreise und Einheit zu, und
 * ein Griff wäre ein Angebot, das die Datenbank hinterher ablehnt.
 *
 * ════════════════════════════════════════════════════════════════════════════
 *  WARUM VORNE MEHR ZU IST ALS HINTEN
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Bei `checked_in` und `checked_out` ist die Anreise keine Planung mehr,
 * sondern ein Vorgang, der stattgefunden hat: der Gast stand an der Tür, der
 * Meldeschein hängt an dieser Buchung, die Ortstaxe zählt ab diesem Tag. Ein
 * Griff, der ihn verschiebt, behauptet etwas nachweislich Falsches — und er
 * täte es mit der beiläufigsten Geste, die diese Anwendung hat. Wer sich beim
 * Datum vertan hat, korrigiert es im Buchungsblatt, wo das Feld sichtbar ist
 * und die Rechnung danebensteht.
 *
 * Hinten ist derselbe Zustand der ANLASS der ganzen Geste: der Gast ist da und
 * fragt an der Tür, ob er zwei Nächte länger bleiben kann.
 *
 * `no_show` bekommt gar keinen: dort hat überhaupt niemand übernachtet. Den
 * Balken zu verlängern hieße, Nächte zu behaupten, die es nicht gab; die Länge
 * einer Nichtanreise ist eine Frage der Stornoregelung, nicht der Belegung.
 *
 * Die Sperre steht bewusst VOR der Rechnungsprüfung: eine Sperre hat weder Gast
 * noch Preis noch Rechnung, und ein Fall, der theoretisch nicht vorkommen kann,
 * soll nicht in einen eigenen Zweig laufen, den nie jemand betritt.
 */
export function gripsFor(
  booking: Pick<Booking, 'kind' | 'status'>, locked: boolean,
): BarGrips {
  if (booking.kind === 'block') return { start: true, end: true }
  if (locked) return { start: false, end: false }
  if (booking.status === 'no_show' || booking.status === 'cancelled') {
    return { start: false, end: false }
  }
  if (booking.status === 'checked_in' || booking.status === 'checked_out') {
    return { start: false, end: true }
  }
  return { start: true, end: true }
}
